// ============================================================
// VALLERJE — Painting Commission: app root
//   0 Welcome · 1 Size · 2 Vision (theme + references)
//   3 Characters · 4 Details · 5 Review · 99 Confirmation
// ============================================================

const INITIAL_FORM = {
  size: null,
  characters: 1,
  theme: "",
  sceneDescription: "",
  referenceLinks: [""],
  name: "",
  email: "",
  country: "",
  socialPlatform: "Instagram",
  socialHandle: "",
  newsletter: false
};

const STORAGE_FORM = "vlj_commission_form";
const STORAGE_STEP = "vlj_commission_step";

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_FORM);
    const step = parseInt(localStorage.getItem(STORAGE_STEP) || "0", 10);
    if (!raw) return { form: INITIAL_FORM, step: 0 };
    // A returning visitor should land back in the flow, never on the
    // confirmation screen of a request they already sent.
    const resumeStep = isNaN(step) || step >= CONFIRM_STEP ? 0 : step;
    return { form: { ...INITIAL_FORM, ...JSON.parse(raw) }, step: resumeStep };
  } catch (e) {
    return { form: INITIAL_FORM, step: 0 };
  }
}

function stepHasSummary(step) {
  // The size grid carries its own prices and the review step is the price,
  // so the running-total panel rides along on everything in between.
  return step >= 2 && step <= 4;
}

function App() {
  const saved = React.useMemo(loadSaved, []);
  const [step, setStep] = React.useState(saved.step);
  const [formData, setFormData] = React.useState(saved.form);
  const [animClass, setAnimClass] = React.useState("step-enter");
  const [faqOpen, setFaqOpen] = React.useState(false);

  React.useEffect(() => {
    try {
      // Once the brief is sent there is no draft left to keep. Clearing here
      // rather than only at submit time means this effect can't re-save the
      // form on the render that follows.
      if (step === CONFIRM_STEP) {
        localStorage.removeItem(STORAGE_FORM);
        localStorage.removeItem(STORAGE_STEP);
        return;
      }
      localStorage.setItem(STORAGE_FORM, JSON.stringify(formData));
      localStorage.setItem(STORAGE_STEP, String(step));
    } catch (e) {}
  }, [formData, step]);

  function updateForm(patch) {
    setFormData(prev => ({ ...prev, ...patch }));
  }

  // ── Browser history ──────────────────────────────────────
  // Each step gets its own history entry so the phone's back gesture and the
  // browser back button step backwards through the form instead of leaving
  // the page. `pushedRef` counts entries we own, so back at the first step
  // still exits to wherever the visitor came from.
  const pushedRef = React.useRef(0);

  React.useEffect(() => {
    try { window.history.replaceState({ vljStep: saved.step }, ""); } catch (e) {}

    function onPop(e) {
      const st = e.state;
      if (!st || typeof st.vljStep !== "number") return; // not ours — let the browser leave
      pushedRef.current = Math.max(0, pushedRef.current - 1);

      // A back press with the FAQ open should close the drawer, not move a step.
      if (!st.vljModal) setFaqOpen(false);

      setAnimClass("step-enter");
      setStep(st.vljStep);
      window.scrollTo({ top: 0 });
    }

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function pushEntry(state) {
    try {
      window.history.pushState(state, "");
      pushedRef.current += 1;
    } catch (e) {}
  }

  function navigate(target, options) {
    const opts = options || {};
    setAnimClass("step-exit");
    setTimeout(() => {
      setStep(target);
      setAnimClass("step-enter");
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (opts.replace) {
        try { window.history.replaceState({ vljStep: target }, ""); } catch (e) {}
      } else {
        pushEntry({ vljStep: target });
      }
    }, 180);
  }

  const goNext = () => navigate(step + 1);
  const goToStep = s => navigate(s);

  // Let the browser own the back move where we have an entry to pop, so the
  // in-form Back button and the hardware/browser back stay in sync.
  function goBack() {
    if (pushedRef.current > 0) {
      window.history.back();
      return;
    }
    navigate(Math.max(0, step - 1));
  }

  function openFaq() {
    // Its own history entry, so back closes the drawer first.
    pushEntry({ vljStep: step, vljModal: true });
    setFaqOpen(true);
  }

  function closeFaq() {
    if (faqOpen && pushedRef.current > 0) {
      window.history.back();
      return;
    }
    setFaqOpen(false);
  }

  function onProgressJump(targetIndex) {
    if (step === CONFIRM_STEP) return;
    if (targetIndex < step) navigate(targetIndex);
  }

  function onSubmitted() {
    // The draft is cleared by the persistence effect once step hits
    // CONFIRM_STEP, so a refresh can't reopen a request already sent.
    // `replace` so a back press can't walk into an already-sent request.
    navigate(CONFIRM_STEP, { replace: true });
  }

  function renderStep() {
    const shared = { formData, updateForm, onNext: goNext, onBack: goBack };
    if (step === CONFIRM_STEP) return h(ConfirmationScreen, { formData });
    if (step === 0) return h(StepWelcome, { onNext: goNext });
    if (step === 1) return h(StepSize, shared);
    if (step === 2) return h(StepVision, shared);
    if (step === 3) return h(StepCharacters, shared);
    if (step === 4) return h(StepDetails, shared);
    if (step === REVIEW_STEP) return h(StepReview, { formData, onBack: goBack, onSubmitted, goToStep });
    return h(StepWelcome, { onNext: goNext });
  }

  const isConfirm = step === CONFIRM_STEP;
  const isReview = step === REVIEW_STEP;
  const showSummary = !isConfirm && stepHasSummary(step) && !!formData.size;
  const showTrust = !isConfirm && !isReview;

  return h("div", { style: { minHeight: "100vh", display: "flex", flexDirection: "column" } },
    !isConfirm && h("header", {
      style: {
        position: "sticky", top: 0, zIndex: 60,
        background: "rgba(34,29,24,0.95)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid #3a3128"
      }
    },
      h("div", { style: { maxWidth: 1200, margin: "0 auto", padding: "16px 24px 14px" } },
        h(ProgressBar, { currentStep: step + 1, labels: STEP_LABELS, onJump: onProgressJump })
      )
    ),

    h("main", {
      style: { flex: 1, maxWidth: 1200, margin: "0 auto", width: "100%", padding: "40px 24px 80px" }
    },
      isConfirm || isReview
        ? h("div", { key: step, className: animClass }, renderStep())
        : h("div", {
            className: showSummary ? "vlj-step-grid vlj-step-grid--with-summary" : "vlj-step-grid",
            style: {
              display: "grid",
              gridTemplateColumns: showSummary ? "1fr 272px" : "1fr",
              gap: 40, alignItems: "start"
            }
          },
            h("div", { key: step, className: animClass },
              renderStep(),
              showTrust && h(TrustStrip, null),
              !isConfirm && h(InlineFAQLink, { onClick: openFaq })
            ),
            showSummary && h("div", { className: "vlj-desktop-only" }, h(SummaryPanel, { formData }))
          )
    ),

    showSummary && h("div", { className: "vlj-mobile-only" }, h(MobileSummarySheet, { formData })),

    !isConfirm && h(FAQDrawer, { isOpen: faqOpen, onClose: closeFaq })
  );
}

(function mountCommissionForm() {
  const el = document.getElementById("vlj-commission-root");
  if (!el) return;
  ReactDOM.createRoot(el).render(h(App, null));
})();
